/**
 * `src/lib/content/versions.ts` — the gate between the corpus on disk and the
 * frozen anchor space. SPEC §3.2's renderer gate, enforced at load time.
 *
 * WHY THIS IS A HARD FAILURE AND NOT A WARNING
 * ────────────────────────────────────────────
 * `anchor_space_version` is the pair (canonical segmentation, frozen word-count
 * fixture). Two builds that disagree about it disagree about what word 8 of
 * 2:255 IS. If the loader rendered a file from a different anchor space anyway,
 * the user could tap a word, mint `hafs:2:255:8-13`, and store a durable,
 * exportable coordinate naming words they never selected — with a `quote` and
 * two hashes captured over the wrong span, so the degradation ladder would read
 * it as tier 1 forever and never flag a thing. There is no recoverable middle
 * ground between "the corpus and the fixture are the same anchor space" and
 * "word anchors are unavailable"; this module draws that line.
 *
 * `corpus_version` is not the anchor space and is checked differently: the
 * frozen module has no opinion about it, so the requirement is INTERNAL
 * CONSISTENCY — meta.json and all thirty juz files must agree with each other —
 * plus the cross-check that the corpus was derived from the same Tanzil bytes
 * the frozen fixture was (`FIXTURE_SOURCE_SHA256`). That second check is the
 * one with teeth: identical version strings over different source bytes is
 * exactly the silent corruption the whole content pipeline exists to prevent.
 */

import {
	ANCHOR_SPACE_VERSION,
	FIXTURE_ANCHOR_SPACE_VERSION,
	FIXTURE_SOURCE_SHA256,
	NUMBERING,
	SEGMENT_VERSION
} from '../anchor';
import {
	ContentLoadError,
	CorpusVersionError,
	type CorpusSource,
	type CorpusVersions
} from './types';

/**
 * What every emitted file must declare, read off the frozen module rather than
 * retyped. Exported so a test can assert the expectation itself, and so
 * `/about/coverage` can show the pair without importing the anchor space.
 */
export const EXPECTED_VERSIONS: Readonly<Omit<CorpusVersions, 'corpus_version'>> = Object.freeze({
	anchor_space_version: ANCHOR_SPACE_VERSION,
	segment_version: SEGMENT_VERSION,
	numbering: NUMBERING
});

/**
 * The sha256 of the vendored Tanzil source the FROZEN FIXTURE was derived from.
 * Re-exported here so the loader's cross-check has one obvious home.
 */
export const EXPECTED_SOURCE_SHA256 = FIXTURE_SOURCE_SHA256;

function requireString(value: unknown, where: string, field: string): string {
	if (typeof value !== 'string' || value === '') {
		throw new ContentLoadError(
			'malformed_shape',
			where,
			`${field} must be a non-empty string, got ${JSON.stringify(value)}`
		);
	}
	return value;
}

/**
 * Assert that a loaded file belongs to the anchor space this build addresses.
 *
 * Throws `CorpusVersionError` — never returns a boolean, never logs and
 * continues. The four checks, in the order a failure is most likely:
 *
 *   1. `anchor_space_version` === `ANCHOR_SPACE_VERSION` (the code's value)
 *   2. `anchor_space_version` === `FIXTURE_ANCHOR_SPACE_VERSION` (the fixture's)
 *   3. `segment_version`      === `SEGMENT_VERSION`
 *   4. `numbering`            === `NUMBERING`
 *
 * 1 and 2 are separate on purpose. They differ when someone bumps the constant
 * in `types.ts` without regenerating `word-counts.json`, or regenerates the
 * fixture without bumping the constant — and in both of those states the corpus
 * can legitimately match one and not the other. Checking only the constant would
 * let a stale fixture through; checking only the fixture would let a stale
 * constant through. `fixtureIsConsistent()` covers the same ground from the
 * anchor side; this is the content side of the same seam.
 */
export function assertAnchorSpace(file: Partial<CorpusVersions>, where: string): void {
	const anchorSpace = requireString(file.anchor_space_version, where, 'anchor_space_version');
	const segment = requireString(file.segment_version, where, 'segment_version');
	const numbering = requireString(file.numbering, where, 'numbering');

	if (anchorSpace !== ANCHOR_SPACE_VERSION) {
		throw new CorpusVersionError(
			'version_mismatch',
			where,
			'anchor_space_version',
			ANCHOR_SPACE_VERSION,
			anchorSpace
		);
	}
	if (anchorSpace !== FIXTURE_ANCHOR_SPACE_VERSION) {
		throw new CorpusVersionError(
			'version_mismatch',
			where,
			'anchor_space_version (frozen word-count fixture)',
			FIXTURE_ANCHOR_SPACE_VERSION,
			anchorSpace
		);
	}
	if (segment !== SEGMENT_VERSION) {
		throw new CorpusVersionError(
			'version_mismatch',
			where,
			'segment_version',
			SEGMENT_VERSION,
			segment
		);
	}
	if (numbering !== NUMBERING) {
		throw new CorpusVersionError('version_mismatch', where, 'numbering', NUMBERING, numbering);
	}
}

/**
 * Assert that a file's `corpus_version` matches the one already established by
 * `meta.json`. A juz file from one build mixed with a meta from another is the
 * shape a half-finished service-worker update takes, and it must not render.
 */
export function assertCorpusVersion(
	file: Partial<CorpusVersions>,
	expected: string,
	where: string
): void {
	const actual = requireString(file.corpus_version, where, 'corpus_version');
	if (actual !== expected) {
		throw new CorpusVersionError(
			'corpus_version_mismatch',
			where,
			'corpus_version',
			expected,
			actual
		);
	}
}

/**
 * Assert the corpus was derived from the same Tanzil bytes the frozen word-count
 * fixture was.
 *
 * This is the check that survives a version string being reused. `corpus_version`
 * is a build identifier and a human can keep one across a source re-pin; the
 * sha256 of the source file cannot be kept across one. If Tanzil publishes a
 * harakat revision and the corpus is rebuilt without regenerating the fixture,
 * the word counts stop describing the text — and the loader must say so here,
 * at load, rather than let 6,236 per-ayah assertions fail one at a time as the
 * user scrolls.
 */
export function assertSourceMatchesFixture(source: CorpusSource | undefined, where: string): void {
	const sha = requireString(source?.sha256, where, 'source.sha256');
	if (sha !== FIXTURE_SOURCE_SHA256) {
		throw new CorpusVersionError(
			'version_mismatch',
			where,
			'source.sha256 (the Tanzil bytes the frozen fixture was built from)',
			FIXTURE_SOURCE_SHA256,
			sha
		);
	}
}
